import React, { useEffect, useState } from 'react';
import { Alert, Button, Nav, Spinner, Table } from 'react-bootstrap';
import { Link, NavLink, Outlet, Route, Routes, useParams } from 'react-router-dom';
import bakingApi from '../services/bakingApi';
import {
  isAiRiggingJobCreationEnabled,
  isAiRiggingUiEnabled,
  isAiRiggingWorkerRuntimeEnabled,
} from '../features/aiRigging';
import {
  hasTenantPermission,
  PROJECT_RIG_MEMORY_VIEW,
  RIGGING_JOB_VIEW,
} from '../auth/permissions';
import { useTenantPermissions } from '../hooks/useTenantPermissions';
import ProjectRiggingJobsPage from './ProjectRiggingJobsPage';
import NewProjectRiggingJobPage from './NewProjectRiggingJobPage';
import ProjectRiggingJobDetailPage from './ProjectRiggingJobDetailPage';
import RiggingRuntimeNotice from '../components/rigging/RiggingRuntimeNotice';

function LifecyclePill({ status }) {
  const label = String(status || 'unknown').replace(/_/g, ' ');
  return <span className={`status-pill status-${status || 'unknown'}`}>{label}</span>;
}

function RiggingShell({ projectId }) {
  return (
    <div data-testid="rigging-shell">
      <p className="mb-2">
        <Link to={`/console/projects/${projectId}`}>← Project</Link>
      </p>
      <h1 className="product-page-title">Rigging</h1>
      <p className="product-page-subtitle mb-3">
        Project Rig Memory is Bakeonator&apos;s persistent Rigging Bible for this project.
        Maya workers remain disposable execution infrastructure.
      </p>
      {!isAiRiggingWorkerRuntimeEnabled() && (
        <RiggingRuntimeNotice className="mb-3" />
      )}
      <Nav variant="tabs" className="mb-3" data-testid="rigging-config-nav">
        <Nav.Item>
          <Nav.Link as={NavLink} to="." end>
            Overview
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link as={NavLink} to="project-memory">
            Project Memory
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link as={NavLink} to="characters">
            Characters
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link
            as={NavLink}
            to={`/console/projects/${projectId}/rigging/jobs`}
            data-testid="rigging-nav-jobs"
            title="Opens the first-class Rigging Jobs surface"
          >
            Jobs
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link as={NavLink} to="decisions">
            Decisions
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link as={NavLink} to="proposals">
            Proposals
          </Nav.Link>
        </Nav.Item>
      </Nav>
      <Outlet />
    </div>
  );
}

function OverviewPanel({ projectId }) {
  const [memory, setMemory] = useState(null);
  const [characters, setCharacters] = useState([]);
  const [policies, setPolicies] = useState([]);
  const [proposals, setProposals] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      let mem = null;
      try {
        mem = await bakingApi.getProjectRigMemory(projectId);
      } catch (err) {
        if (err.status !== 404) throw err;
      }
      const [chars, pols, props] = await Promise.all([
        bakingApi.listRigCharacters(projectId).catch(() => []),
        bakingApi.listRigProjectPolicies(projectId).catch(() => []),
        bakingApi.listRigProposals(projectId).catch(() => []),
      ]);
      setMemory(mem);
      setCharacters(chars || []);
      setPolicies(pols || []);
      setProposals(props || []);
    } catch (err) {
      setError(err.message || 'Failed to load Project Rig Memory');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [projectId]);

  const createMemory = async () => {
    setCreating(true);
    setError(null);
    try {
      await bakingApi.createProjectRigMemory(projectId, {});
      await load();
    } catch (err) {
      setError(err.message || 'Failed to create Project Rig Memory');
    } finally {
      setCreating(false);
    }
  };

  if (loading) {
    return (
      <div className="py-4">
        <Spinner animation="border" size="sm" /> Loading…
      </div>
    );
  }

  if (error) {
    return <div className="error-state">{error}</div>;
  }

  if (!memory) {
    return (
      <div className="product-card" data-testid="rigging-unconfigured">
        <h2 className="h5">Project Rig Memory</h2>
        <p className="text-muted">
          No Project Rig Memory exists yet. Create one to store conventions, characters, and
          published rigging specifications.
        </p>
        <Button onClick={createMemory} disabled={creating}>
          {creating ? 'Creating…' : 'Create Project Rig Memory'}
        </Button>
      </div>
    );
  }

  const draftsInReview = [
    ...policies.filter((p) => p.lifecycle_status === 'in_review'),
    ...proposals.filter((p) => ['proposed', 'under_review'].includes(p.lifecycle_status)),
  ];

  return (
    <div data-testid="rigging-overview">
      <div className="product-card mb-3">
        <h2 className="h5">Status</h2>
        <dl className="row mb-0">
          <dt className="col-sm-4">Project Rig Memory</dt>
          <dd className="col-sm-8">
            <LifecyclePill status={memory.status} /> {memory.display_name}
          </dd>
          <dt className="col-sm-4">Active project policy</dt>
          <dd className="col-sm-8 mono">
            {memory.active_project_policy_version || '—'}
          </dd>
          <dt className="col-sm-4">Characters</dt>
          <dd className="col-sm-8">{characters.length}</dd>
          <dt className="col-sm-4">Awaiting review</dt>
          <dd className="col-sm-8">{draftsInReview.length}</dd>
        </dl>
      </div>
      <div className="product-card">
        <h2 className="h5">Characters</h2>
        {characters.length === 0 ? (
          <p className="text-muted mb-0">No characters defined yet.</p>
        ) : (
          <Table responsive size="sm" className="mb-0">
            <thead>
              <tr>
                <th>Name</th>
                <th>Type</th>
                <th>Status</th>
                <th>Active version</th>
              </tr>
            </thead>
            <tbody>
              {characters.map((c) => (
                <tr key={c.id}>
                  <td>
                    <Link to={`characters/${c.id}`}>{c.display_name}</Link>
                  </td>
                  <td>{c.character_type}</td>
                  <td>
                    <LifecyclePill status={c.status} />
                  </td>
                  <td className="mono">{c.active_memory_version || '—'}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </div>
    </div>
  );
}

function ProjectMemoryPanel({ projectId }) {
  const [policies, setPolicies] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await bakingApi.listRigProjectPolicies(projectId);
      setPolicies(data || []);
    } catch (err) {
      setError(err.message || 'Failed to load project policies');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [projectId]);

  const createDraft = async () => {
    setBusy(true);
    setError(null);
    try {
      await bakingApi.createRigProjectPolicy(projectId, {
        change_summary: 'Initial project policy draft',
        configuration: {
          schema_version: 1,
          project: {
            maya_workspace_root: '.',
            units: 'centimeter',
            up_axis: 'y',
            forward_axis: 'x',
          },
          directories: {
            source_scenes: 'scenes/source',
            rig_scenes: 'scenes/rig',
            models: 'assets/models',
            skeletons: 'assets/skeletons',
            animations: 'assets/animations',
            exports: 'exports',
            reports: 'reports',
          },
          naming: {
            joint_pattern: '{side}{region}{index}jnt',
            control_pattern: '{side}{region}_{index}ctrl',
            geometry_pattern: '{character}{part}_geo',
          },
          runtime: {
            allowed_maya_versions: ['2026'],
            allowed_fabricatorstudio_versions: ['1.0.0'],
          },
          validation_defaults: {
            reject_unknown_nodes: true,
            reject_script_nodes: true,
            maximum_skin_influences: 4,
          },
        },
      });
      await load();
    } catch (err) {
      setError(err.message || 'Failed to create draft');
    } finally {
      setBusy(false);
    }
  };

  const publish = async (versionId) => {
    setBusy(true);
    setError(null);
    try {
      await bakingApi.publishRigProjectPolicy(projectId, versionId);
      await load();
    } catch (err) {
      setError(err.message || 'Failed to publish');
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <Spinner animation="border" size="sm" />;
  if (error) return <div className="error-state">{error}</div>;

  return (
    <div data-testid="rigging-project-memory">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2 className="h5 mb-0">Project policy versions</h2>
        <Button size="sm" onClick={createDraft} disabled={busy}>
          Create draft
        </Button>
      </div>
      <Table responsive size="sm">
        <thead>
          <tr>
            <th>Version</th>
            <th>Lifecycle</th>
            <th>SHA-256</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {policies.map((p) => (
            <tr key={p.id}>
              <td>{p.version_number || '—'}</td>
              <td>
                <LifecyclePill status={p.lifecycle_status} />
                {p.lifecycle_status === 'published' && (
                  <span className="ms-2 text-muted small">immutable</span>
                )}
              </td>
              <td className="mono small">{(p.configuration_sha256 || '').slice(0, 12)}…</td>
              <td>
                {p.lifecycle_status === 'draft' && (
                  <Button
                    size="sm"
                    variant="outline-primary"
                    disabled={busy}
                    onClick={() => publish(p.id)}
                  >
                    Publish
                  </Button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
}

function CharactersPanel({ projectId }) {
  const [characters, setCharacters] = useState([]);
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      setCharacters((await bakingApi.listRigCharacters(projectId)) || []);
    } catch (err) {
      setError(err.message || 'Failed to load characters');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [projectId]);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await bakingApi.createRigCharacter(projectId, {
        display_name: name,
        slug,
        character_type: 'biped',
      });
      setName('');
      setSlug('');
      await load();
    } catch (err) {
      setError(err.message || 'Failed to create character');
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <Spinner animation="border" size="sm" />;

  return (
    <div data-testid="rigging-characters">
      {error && <div className="error-state mb-2">{error}</div>}
      <form className="product-card mb-3" onSubmit={create}>
        <h2 className="h5">Create character</h2>
        <div className="row g-2">
          <div className="col-md-4">
            <input
              className="form-control"
              placeholder="Display name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </div>
          <div className="col-md-4">
            <input
              className="form-control"
              placeholder="slug"
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              required
            />
          </div>
          <div className="col-md-4">
            <Button type="submit" disabled={busy}>
              Create
            </Button>
          </div>
        </div>
      </form>
      <Table responsive size="sm">
        <thead>
          <tr>
            <th>Character</th>
            <th>Slug</th>
            <th>Type</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {characters.map((c) => (
            <tr key={c.id}>
              <td>
                <Link to={`${c.id}`}>{c.display_name}</Link>
              </td>
              <td className="mono">{c.slug}</td>
              <td>{c.character_type}</td>
              <td>
                <LifecyclePill status={c.status} />
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
}

function CharacterDetailPanel({ projectId, characterId }) {
  const [character, setCharacter] = useState(null);
  const [versions, setVersions] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [c, v] = await Promise.all([
          bakingApi.getRigCharacter(projectId, characterId),
          bakingApi.listRigCharacterVersions(projectId, characterId),
        ]);
        if (!cancelled) {
          setCharacter(c);
          setVersions(v || []);
        }
      } catch (err) {
        if (!cancelled) setError(err.message || 'Failed to load character');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [projectId, characterId]);

  if (loading) return <Spinner animation="border" size="sm" />;
  if (error) return <div className="error-state">{error}</div>;
  if (!character) return null;

  return (
    <div data-testid="rigging-character-detail">
      <p>
        <Link to="..">← Characters</Link>
      </p>
      <h2 className="h5">{character.display_name}</h2>
      <p className="text-muted">
        <LifecyclePill status={character.status} /> · {character.character_type}
      </p>
      <h3 className="h6">Memory versions</h3>
      <Table responsive size="sm">
        <thead>
          <tr>
            <th>Version</th>
            <th>Lifecycle</th>
            <th>SHA-256</th>
          </tr>
        </thead>
        <tbody>
          {versions.map((v) => (
            <tr key={v.id}>
              <td>
                <Link to={`versions/${v.id}`}>{v.version_number || 'draft'}</Link>
              </td>
              <td>
                <LifecyclePill status={v.lifecycle_status} />
                {v.lifecycle_status === 'published' && (
                  <span className="ms-2 text-muted small">immutable</span>
                )}
              </td>
              <td className="mono small">{(v.configuration_sha256 || '').slice(0, 12)}…</td>
            </tr>
          ))}
        </tbody>
      </Table>
      {!isAiRiggingJobCreationEnabled() && (
        <Alert variant="info">
          Rigging job creation is feature-gated. Published memory can be prepared now; Maya
          execution remains unavailable until the runtime flag is enabled.
        </Alert>
      )}
    </div>
  );
}

function SimpleListPanel({ title, loader, testId }) {
  const [rows, setRows] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await loader();
        if (!cancelled) setRows(data || []);
      } catch (err) {
        if (!cancelled) setError(err.message || 'Failed to load');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loader]);

  if (loading) return <Spinner animation="border" size="sm" />;
  if (error) return <div className="error-state">{error}</div>;

  return (
    <div data-testid={testId}>
      <h2 className="h5">{title}</h2>
      {rows.length === 0 ? (
        <p className="text-muted">None yet.</p>
      ) : (
        <Table responsive size="sm">
          <thead>
            <tr>
              <th>Id</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id}>
                <td className="mono small">{r.id}</td>
                <td>
                  <LifecyclePill status={r.lifecycle_status || r.status} />
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </div>
  );
}

function CharacterDetailRoute({ projectId }) {
  const { characterId } = useParams();
  return <CharacterDetailPanel projectId={projectId} characterId={characterId} />;
}

export default function ProjectRiggingPage() {
  const { projectId } = useParams();
  const { permissions, hasTenantPermission: can } = useTenantPermissions();

  if (!isAiRiggingUiEnabled()) {
    return (
      <div data-testid="rigging-feature-disabled">
        <Alert variant="secondary">AI Rigging is not enabled in this environment.</Alert>
        <Link to={`/console/projects/${projectId}`}>← Back to project</Link>
      </div>
    );
  }

  const canViewSection =
    can(PROJECT_RIG_MEMORY_VIEW) ||
    can(RIGGING_JOB_VIEW) ||
    hasTenantPermission(permissions, PROJECT_RIG_MEMORY_VIEW) ||
    hasTenantPermission(permissions, RIGGING_JOB_VIEW);
  if (!canViewSection) {
    return (
      <div className="error-state" data-testid="rigging-forbidden">
        You do not have permission to view Rigging for this project.
      </div>
    );
  }

  return (
    <Routes>
      {/* First-class Rigging Jobs surface — outside Rigging configuration shell */}
      <Route path="jobs" element={<ProjectRiggingJobsPage />} />
      <Route path="jobs/new" element={<NewProjectRiggingJobPage />} />
      <Route path="jobs/:jobId" element={<ProjectRiggingJobDetailPage />} />
      <Route element={<RiggingShell projectId={projectId} />}>
        <Route index element={<OverviewPanel projectId={projectId} />} />
        <Route path="project-memory" element={<ProjectMemoryPanel projectId={projectId} />} />
        <Route path="characters" element={<CharactersPanel projectId={projectId} />} />
        <Route
          path="characters/:characterId"
          element={<CharacterDetailRoute projectId={projectId} />}
        />
        <Route
          path="characters/:characterId/versions/:versionId"
          element={<CharacterDetailRoute projectId={projectId} />}
        />
        <Route
          path="decisions"
          element={
            <SimpleListPanel
              title="Decisions"
              testId="rigging-decisions"
              loader={() => bakingApi.listRigDecisions(projectId)}
            />
          }
        />
        <Route
          path="proposals"
          element={
            <SimpleListPanel
              title="Proposals"
              testId="rigging-proposals"
              loader={() => bakingApi.listRigProposals(projectId)}
            />
          }
        />
      </Route>
    </Routes>
  );
}
